// layers/base/app/composables/useLocaleSync.ts
//
// Single entry point for changing the UI language. Keeps the active vue-i18n
// locale, the `i18n_locale` cookie, and (later) the backend in sync.
// The cookie is the source of truth for SSR first paint so server and client
// agree on the locale and never hydration-mismatch.
import { computed } from 'vue'
import { normalizeLocale } from '../i18n/normalizeLocale'

export function useLocaleSync() {
  const { locale, setLocale: applyLocale } = useI18n()
  const switchLocalePath = useSwitchLocalePath()
  const route = useRoute()
  const cookie = useCookie<string | null>('i18n_locale', {
    maxAge: 60 * 60 * 24 * 365,
    sameSite: 'lax',
    path: '/',
  })

  const currentLocale = computed(() => locale.value)

  async function setLocale(raw: string): Promise<void> {
    const code = normalizeLocale(raw)
    cookie.value = code
    // Under a prefix strategy the localized path differs from the current
    // route → navigate so the locale lives in the URL. Under `no_prefix`
    // (web) switchLocalePath returns the current path → skip navigation.
    const target = switchLocalePath(code)
    if (target && target !== route.path && target !== route.fullPath) {
      await navigateTo(target)
    }
    await applyLocale(code)
    // Later stage hook: if authenticated, PATCH users.language here.
  }

  return { currentLocale, setLocale }
}
